// "Atlanta Editorial" design-refresh tokens (raw values, used via inline
// styles alongside the Tailwind class map below). Source of truth:
// design_handoff_calendar_views/README.md — peach (light) / peachDark (dark).
const editorialFonts = {
    ui: '"Hanken Grotesk", system-ui, sans-serif',
    display: '"Newsreader", Georgia, serif',
    mono: '"IBM Plex Mono", monospace',
};

const editorialLight = {
    mode: 'light',
    fonts: editorialFonts,
    colors: {
        bg: '#e6e9ef',
        surface: '#f6f8fb',
        surfaceAlt: '#eef1f6',
        gridLine: '#d3d9e3',
        ink: '#1e2430',
        muted: '#697081',
        faint: '#9aa3b4',
        accent: 'oklch(0.53 0.19 25)',     // Atlanta red
        accentSoft: 'oklch(0.93 0.05 28)',
        headBg: '#212734',
        headInk: '#e6e9ef',
        today: 'oklch(0.53 0.19 25)',
        todayInk: '#f6f8fb',
        selBg: 'oklch(0.95 0.035 25)',
        selRing: 'oklch(0.53 0.19 25)',
        chipL: 0.96, chipC: 0.03,
        chipTextL: 0.36, chipTextC: 0.09,
    },
};

const editorialDark = {
    mode: 'dark',
    fonts: editorialFonts,
    colors: {
        bg: '#0f131a',
        surface: '#181d27',
        surfaceAlt: '#141a23',
        gridLine: '#2a313e',
        ink: '#e7ebf3',
        muted: '#99a1b3',
        faint: '#646c7e',
        accent: 'oklch(0.62 0.2 27)',
        accentSoft: 'oklch(0.33 0.09 27)',
        headBg: '#0b0e14',
        headInk: '#e7ebf3',
        today: 'oklch(0.62 0.2 27)',
        todayInk: '#1a1410',
        selBg: 'oklch(0.31 0.08 27)',
        selRing: 'oklch(0.62 0.2 27)',
        chipL: 0.34, chipC: 0.055,
        chipTextL: 0.85, chipTextC: 0.05,
    },
};

export const themes = {
    light: {
        tokens: editorialLight,
        background: 'bg-white',
        text: 'text-gray-900',
        secondaryText: 'text-gray-600',
        headerBg: 'bg-gray-100',
        headerText: 'text-gray-800',
        dayBg: 'bg-slate-100',
        dayHeaderBg: 'bg-slate-300',
        inactiveDayBg: 'bg-slate-700',
        gridBg: 'bg-blue-100',
        divider: 'divide-gray-300',
        todayBg: 'bg-yellow-200',
        buttonHover: 'hover:text-gray-800',
        eventBg: 'bg-blue-100',
        eventHeaderBg: 'bg-blue-200',
        eventBorder: 'border-blue-300',
        eventText: 'text-gray-900',
        modalBg: 'bg-white',
        modalOverlay: 'bg-black bg-opacity-50',
        link: 'text-blue-600 hover:text-blue-800',
        border: 'border-gray-200',
        tag: 'bg-blue-100 text-blue-800',
        loadingBg: 'bg-white',
        loadingText: 'text-gray-900',
        alertBackground: 'bg-red-200',
        weekendHeaderBg: 'bg-slate-700',
        weekdayHeaderBg: 'bg-slate-600',
        dayNameText: 'text-slate-100',
        eventCountBg: 'bg-blue-300',
        button: 'bg-gray-400 !text-white',
        todayBackground: 'bg-gray-600 text-white',
        featuredDay: 'bg-yellow-100 italic',
        todayBarBackground: 'bg-gray-200',
        // Detail pane
        detailPaneBg: 'bg-[#eef1f6]',
        panelBorder: 'border-[#d3d9e3]',
        // List view
        cardBg: 'bg-gray-50',
        actionButton: 'bg-blue-500 hover:bg-blue-600 text-white',
        expandButton: 'bg-blue-100 hover:bg-blue-200 text-blue-700',
        // Filter buttons
        favoritesActive: 'bg-red-100 text-red-700 border-red-300',
        hidePrivateActive: 'bg-blue-100 text-blue-700 border-blue-300',
        filterInactive: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200',
        // Nav chrome — Atlanta Editorial: active view = accent fill, inactive = muted/transparent
        viewButtonActive: 'bg-[oklch(0.53_0.19_25)] text-white shadow-sm',
        viewButtonInactive: 'bg-transparent hover:bg-[#eef1f6] text-[#697081]',
        navLink: 'text-[#697081] hover:text-[#1e2430]',
        pickerBg: 'bg-white border-gray-200',
        pickerHover: 'hover:bg-[#eef1f6]',
        navArrowHover: 'hover:bg-[#eef1f6]',
    },
    dark: {
        tokens: editorialDark,
        background: 'bg-gray-900',
        text: 'text-gray-100',
        secondaryText: 'text-gray-300',
        headerBg: 'bg-gray-800',
        headerText: 'text-gray-100',
        dayBg: 'bg-gray-800',
        dayHeaderBg: 'bg-gray-700',
        inactiveDayBg: 'bg-gray-900',
        gridBg: 'bg-gray-700',
        divider: 'divide-gray-900',
        todayBg: 'bg-yellow-300/30',
        buttonHover: 'hover:text-white',
        eventBg: 'bg-blue-900',
        eventHeaderBg: 'bg-blue-800',
        eventBorder: 'border-blue-600',
        eventText: 'text-gray-100',
        modalBg: 'bg-gray-800',
        modalOverlay: 'bg-black bg-opacity-70',
        link: 'text-slate-400 hover:text-slate-300',
        border: 'border-gray-700',
        tag: 'bg-slate-900 text-slate-200',
        loadingBg: 'bg-gray-800',
        loadingText: 'text-gray-100',
        alertBackground: 'bg-yellow-900',
        weekendHeaderBg: 'bg-slate-900',
        weekdayHeaderBg: 'bg-slate-600',
        dayNameText: 'text-slate-100',
        eventCountBg: 'bg-blue-900',
        button: 'bg-gray-400 !text-white',
        todayBackground: 'bg-gray-300 text-black',
        featuredDay: 'bg-yellow-900/40 italic',
        todayBarBackground: 'bg-gray-700',
        // Detail pane
        detailPaneBg: 'bg-[#141a23]',
        panelBorder: 'border-[#2a313e]',
        // List view
        cardBg: 'bg-gray-800',
        actionButton: 'bg-blue-700 hover:bg-blue-600 text-white',
        expandButton: 'bg-slate-700 hover:bg-slate-600 text-slate-200',
        // Filter buttons
        favoritesActive: 'bg-red-900/40 text-red-300 border-red-700',
        hidePrivateActive: 'bg-blue-900/40 text-blue-300 border-blue-700',
        filterInactive: 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600',
        // Nav chrome — Atlanta Editorial: active view = accent fill, inactive = muted/transparent
        viewButtonActive: 'bg-[oklch(0.62_0.2_27)] text-[#1a1410] shadow-sm',
        viewButtonInactive: 'bg-transparent hover:bg-[#141a23] text-[#99a1b3]',
        navLink: 'text-[#99a1b3] hover:text-[#e7ebf3]',
        pickerBg: 'bg-gray-800 border-gray-700',
        pickerHover: 'hover:bg-[#141a23]',
        navArrowHover: 'hover:bg-[#141a23]',
    }
};
